import { useQuery } from "@apollo/client"
import { useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { GraphqlError } from "~/ui/errors"
import Text from "~/ui/typography"
import { useDocumentTitle } from "~/common/use-document-title"
import { ScreenLoading } from "~/ui/screen-loading"
import { LettermemeFooter } from "./lettermeme-footer"
import { NewsletterView } from "./newsletter-view"
import { useTrackingCode } from "~/common/use-tracking-code"

const lettermemeQuery = gql(/* GraphQL */ `
  query lettermeme($urlPath: String!) {
    lettermeme(urlPath: $urlPath) {
      ...LettermemeView
      latestNewsletter {
        __typename
        ...NewsletterView
      }
    }
  }
`)

const viewerOwnedLettermemesQuery = gql(/* GraphQL */ `
  query ViewerOwnedLettermemesForLettermemeScreen {
    viewer {
      id
      ownedLettermemes {
        id
      }
    }
  }
`)

export const LettermemeScreen: React.FC = () => {
  const { urlPath } = useParams<{ urlPath: string }>()
  const { viewer } = useViewerMaybe()
  const { data, error, loading } = useQuery(lettermemeQuery, {
    variables: { urlPath: urlPath! },
    skip: !urlPath,
  })

  const lettermemeId = data?.lettermeme?.id
  const { data: ownedData } = useQuery(viewerOwnedLettermemesQuery, {
    fetchPolicy: "cache-first",
    skip: !viewer,
  })

  useDocumentTitle(data?.lettermeme?.name || "Lettermeme")
  useTrackingCode(data?.lettermeme?.trackingCode)
  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!data?.lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Lettermeme not found
        </Text>
        <Text>The lettermeme you're looking for doesn't exist.</Text>
      </div>
    )
  }

  const lettermeme = data.lettermeme
  const newsletter = lettermeme.latestNewsletter
  const isOwner = Boolean(
    viewer &&
      lettermemeId &&
      ownedData?.viewer?.ownedLettermemes?.some(
        (ownedLettermeme) => ownedLettermeme.id === lettermemeId
      )
  )
  const isPaidLocked =
    lettermeme.monetizationEnabled && lettermeme.viewerIsSubscribed !== true && !isOwner
  const isFreeLocked = !viewer && !lettermeme.monetizationEnabled
  const isPreview = isPaidLocked || isFreeLocked

  if (!newsletter || newsletter.__typename !== "NewsletterSummary") {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Newsletter not found
        </Text>
        <Text>The newsletter you're looking for doesn't exist.</Text>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <NewsletterView
        lettermeme={lettermeme}
        newsletter={newsletter}
        variant={isPreview ? "preview" : "full"}
      />
      <LettermemeFooter />
    </div>
  )
}
